import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ISetDimensionsEntityGroupingPayload, ISetEntityGroupingPayload } from 'core/models/report-response';
import { pendoSelector } from 'redux-v2/global-store/global-store.selector';
import {
  createEntityGroupingActions,
  createEntityGroupingSelectors,
} from 'core/utils/entity-grouping.util';
import { SIERAPage } from 'core/constants/page-url';

/**
 * Custom hook for entity grouping operations
 *
 * Responsibilities:
 * - Abstracts store-specific logic (Report vs ReportBuilder)
 * - Provides grouped entity actions
 * - Handles API calls and state updates
 *
 * Implementation:
 * - Uses store-agnostic action creators
 * - Memoizes selectors and actions for performance
 */
export const useEntityGrouping = (setGrouping = true) => {
  const dispatch = useDispatch();
  const selectedPage = useSelector(pendoSelector) as SIERAPage;

  const selectors = useMemo(() => createEntityGroupingSelectors(selectedPage), [selectedPage]);
  const actions = useMemo(() => createEntityGroupingActions(selectedPage), [selectedPage]);
  const reportId = useSelector(selectors.reportIdSelector);

  const getAndApplyEntityGrouping = useCallback(async (params: ISetDimensionsEntityGroupingPayload) => {
    const metaResult = await dispatch(actions.getMetaAction({ ...params, reportId }));
    if (setGrouping && actions.setGroupingAction) {
      dispatch(actions.setGroupingAction({
        ...params,
        res: metaResult.payload,
      }));
    }
  }, [dispatch, actions.getMetaAction, actions.setGroupingAction]);

  const updateEntityGrouping = useCallback((params: ISetEntityGroupingPayload) => {
    dispatch(actions.updateGroupingAction(params));
  }, [dispatch, actions.updateGroupingAction]);

  return {
    getAndApplyEntityGrouping,
    updateEntityGrouping,
    selectors,
  };
};

export default useEntityGrouping;
